import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  AuthFlowType,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { clearToken, getDecryptedToken, storeEncryptedToken } from '../../../services/tokenStorage';


const client = new CognitoIdentityProviderClient({ region: 'us-east-1' });
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

function isTokenExpired(token: string): boolean {
  try {
    const [, payloadBase64] = token.split('.');
    const payload = JSON.parse(atob(payloadBase64));
    const expiry = payload.exp;
    const now = Math.floor(Date.now() / 1000);
    return now >= expiry;
  } catch {
    return true;
  }
}

async function login(username: string, password: string) {
  const command = new InitiateAuthCommand({
    AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
    ClientId: clientId,
    AuthParameters: {
      USERNAME: username,
      PASSWORD: password,
    },
  });

  const response = await client.send(command);

  if (response.AuthenticationResult?.AccessToken) {
    storeEncryptedToken('accessToken', response.AuthenticationResult.AccessToken || '');
    storeEncryptedToken('idToken', response.AuthenticationResult.IdToken || '');
    localStorage.setItem('accessToken', response.AuthenticationResult.AccessToken || ''); // Endpoint testing
    return { token: response.AuthenticationResult.AccessToken };
  }

  if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    localStorage.setItem('challengeSession', response.Session || '');
    localStorage.setItem('challengeUsername', username);
    return { newPasswordRequired: true };
  }
}

function logout(message?: string) {
  if (message) {
    localStorage.setItem('sessionMessage', JSON.stringify(message));
  } else {
    localStorage.removeItem('sessionMessage');
  }

  clearToken('accessToken');
  clearToken('idToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

async function requestPasswordReset(username: string) {
  const command = new ForgotPasswordCommand({
    ClientId: clientId,
    Username: username,
  });
  return await client.send(command);
}

async function confirmPasswordReset(username: string, code: string, newPassword: string) {
  const command = new ConfirmForgotPasswordCommand({
    ClientId: clientId,
    Username: username,
    ConfirmationCode: code,
    Password: newPassword,
  });
  return await client.send(command);
}

const storedToken = getDecryptedToken('accessToken');

export { login, logout, isTokenExpired, requestPasswordReset, confirmPasswordReset, storedToken };
